/**
 * AllowlistField — read-only chip display + CSV import/export toolbar.
 *
 * v1 known gaps:
 *   - onAdd / onRemove are intentionally no-ops: the PUT /api/engagements/{id}
 *     endpoint does not accept allowlist edits, and there is no individual-item
 *     DELETE endpoint. Only CSV import (preview → commit) is supported server-side.
 *   - Chip "×" buttons are rendered but disabled to communicate this constraint to
 *     operators. Remove support requires a future dedicated allowlist item endpoint.
 */

import { useRef, useState } from "react";
import { CsvImportBar } from "./CsvImportBar";

interface AllowlistFieldProps {
  /** Accessible label for the field (not rendered visibly; used by callers for context). */
  label?: string;
  hint?: string;
  entries: string[];
  /**
   * Called when a CSV file is selected for preview.
   * Returns a preview token + counts for CsvImportBar to display.
   */
  onPreview: (file: File) => Promise<{
    preview_token: string;
    validCount: number;
    duplicateCount: number;
    invalidCount: number;
    totalRows: number;
  }>;
  /** Called with the preview_token when the operator clicks Commit. */
  onCommit: (preview_token: string) => Promise<void>;
  /** CSV export filename (e.g. "phones.csv") */
  exportFilename?: string;
}

export function AllowlistField({
  hint,
  entries,
  onPreview,
  onCommit,
  exportFilename = "allowlist.csv",
}: AllowlistFieldProps) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [previewState, setPreviewState] = useState<{
    token: string;
    validCount: number;
    duplicateCount: number;
    invalidCount: number;
    totalRows: number;
  } | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [importError, setImportError] = useState<string | null>(null);

  async function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setPreviewing(true);
    setImportError(null);
    setPreviewState(null);
    try {
      const result = await onPreview(file);
      setPreviewState({
        token: result.preview_token,
        validCount: result.validCount,
        duplicateCount: result.duplicateCount,
        invalidCount: result.invalidCount,
        totalRows: result.totalRows,
      });
    } catch (err) {
      setImportError(err instanceof Error ? err.message : "Preview failed");
    } finally {
      setPreviewing(false);
      // Reset file input so the same file can be re-selected after discard
      if (fileRef.current) fileRef.current.value = "";
    }
  }

  async function handleCommit() {
    if (!previewState) return;
    setCommitting(true);
    setImportError(null);
    try {
      await onCommit(previewState.token);
      setPreviewState(null);
    } catch (err) {
      setImportError(err instanceof Error ? err.message : "Commit failed");
    } finally {
      setCommitting(false);
    }
  }

  function handleExport() {
    const csv = entries.join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = exportFilename;
    a.click();
    URL.revokeObjectURL(url);
  }

  const btnStyle: React.CSSProperties = {
    padding: "4px 10px",
    borderRadius: 6,
    border: "1px solid var(--border)",
    background: "var(--bg-tertiary)",
    color: "var(--text-secondary)",
    fontSize: 10.5,
    fontWeight: 600,
    cursor: "pointer",
    fontFamily: "var(--font-sans)",
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      {/* Toolbar */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          flexWrap: "wrap",
          paddingBottom: 8,
          borderBottom: "1px dashed var(--border-subtle)",
        }}
      >
        <span
          style={{
            fontFamily: "var(--font-mono)",
            fontSize: 10,
            color: "var(--text-muted)",
          }}
        >
          {entries.length} {entries.length === 1 ? "entry" : "entries"}
        </span>
        <div style={{ marginLeft: "auto", display: "flex", gap: 6 }}>
          <button
            style={btnStyle}
            onClick={() => fileRef.current?.click()}
            disabled={previewing}
          >
            ↑ import CSV
          </button>
          <button style={btnStyle} onClick={handleExport} disabled={entries.length === 0}>
            ↓ export CSV
          </button>
        </div>
      </div>

      {/* Hidden file input */}
      <input
        ref={fileRef}
        type="file"
        accept=".csv,text/csv"
        style={{ display: "none" }}
        onChange={(e) => { void handleFileChange(e); }}
      />

      {/* Chips */}
      {entries.length > 0 ? (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
          {entries.map((entry) => (
            <span
              key={entry}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                background: "var(--bg-tertiary)",
                border: "1px solid var(--border)",
                borderRadius: 100,
                padding: "4px 10px",
                fontFamily: "var(--font-mono)",
                fontSize: 10.5,
                color: "var(--text-secondary)",
              }}
            >
              {entry}
              {/* Remove is a no-op in v1 — no individual-item delete endpoint */}
              <span
                title="Chip removal not yet supported (no API endpoint). Use CSV import to replace the full list."
                style={{
                  color: "var(--text-muted)",
                  fontWeight: 700,
                  cursor: "not-allowed",
                  opacity: 0.4,
                  userSelect: "none",
                }}
              >
                ×
              </span>
            </span>
          ))}
        </div>
      ) : (
        <div
          style={{
            fontSize: 11,
            color: "var(--text-muted)",
            fontStyle: "italic",
          }}
        >
          No entries — import a CSV to populate.
        </div>
      )}

      {/* CSV preview bar */}
      {previewing && (
        <div style={{ fontSize: 11, color: "var(--text-muted)", fontFamily: "var(--font-mono)" }}>
          Parsing CSV…
        </div>
      )}
      {previewState && (
        <CsvImportBar
          totalRows={previewState.totalRows}
          validCount={previewState.validCount}
          duplicateCount={previewState.duplicateCount}
          invalidCount={previewState.invalidCount}
          loading={committing}
          error={importError}
          onCommit={() => { void handleCommit(); }}
          onDiscard={() => {
            setPreviewState(null);
            setImportError(null);
          }}
        />
      )}
      {importError && !previewState && (
        <div style={{ fontSize: 11, color: "var(--error)" }}>{importError}</div>
      )}

      {/* CSV format hint */}
      {hint && (
        <div
          style={{
            background: "var(--bg-input)",
            border: "1px dashed var(--border)",
            borderRadius: 6,
            padding: "8px 10px",
            fontFamily: "var(--font-mono)",
            fontSize: 10,
            color: "var(--text-muted)",
            lineHeight: 1.6,
            whiteSpace: "pre-wrap",
          }}
        >
          {hint}
        </div>
      )}
    </div>
  );
}
